import { useCallback, useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'
import { parseSkillFrontmatter, splitKeywords } from '../../../lib/parseSkillFrontmatter'
import { AssignUsersDrawer } from './skills/AssignUsersDrawer'
import { UploadSkillWizard } from './skills/UploadSkillWizard'
import { ConfirmationDialog } from '../../shared/ConfirmationDialog'

const API_BASE = import.meta.env.VITE_API_BASE_URL || ''

type SkillTab = 'all' | 'mine' | 'assigned'

type SkillListItem = {
  id: string
  owner_id: string
  name: string
  description: string
  created_at: string | null
  updated_at: string | null
  is_owner: boolean
  is_enabled?: boolean
}

type SkillDetail = {
  id: string
  owner_id: string
  name: string
  description: string
  skill_md: string
  created_at: string | null
  updated_at: string | null
  is_enabled?: boolean
}

type AssignmentRow = {
  user_id: string
  email: string | null
  username: string | null
  created_at: string | null
}

function formatShortDate(iso: string | null | undefined): string {
  if (!iso) return '—'
  try {
    const d = new Date(iso)
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
  } catch {
    return '—'
  }
}

function initialsFromUser(u: { username?: string | null; email?: string | null }): string {
  const s = (u.username || u.email || '?').trim()
  const parts = s.split(/[\s@._-]+/).filter(Boolean)
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase().slice(0, 2)
  return s.slice(0, 2).toUpperCase()
}

const AV_COLORS = ['#6366f1', '#8b5cf6', '#ec4899', '#14b8a6', '#f59e0b', '#22c55e']

function CreateSkillModal({ onClose, onCreated }: { onClose: () => void; onCreated: (id: string) => void }) {
  type Step = 1 | 2 | 3
  const [skillMd, setSkillMd] = useState('')
  const [step, setStep] = useState<Step>(1)
  const [vr, setVr] = useState<{ valid?: boolean; errors?: string[]; warnings?: string[] } | null>(null)
  const [validating, setValidating] = useState(false)
  const [saving, setSaving] = useState(false)

  const validateNow = async (t: string): Promise<{ ok: boolean; vdata: { valid?: boolean; errors?: string[]; warnings?: string[] } }> => {
    setValidating(true)
    setVr(null)
    try {
      const vres = await fetch(`${API_BASE}api/skills/validate-text`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ skill_md: t }),
      })
      const vdata = (await vres.json().catch(() => ({}))) as { valid?: boolean; errors?: string[]; warnings?: string[] }
      if (!vres.ok) {
        const out = { valid: false, errors: ['Validation request failed'], warnings: [] as string[] }
        setVr(out)
        return { ok: false, vdata: out }
      }
      setVr(vdata)
      return { ok: vdata.valid !== false, vdata }
    } catch {
      const out = { valid: false, errors: ['Network error'], warnings: [] as string[] }
      setVr(out)
      return { ok: false, vdata: out }
    } finally {
      setValidating(false)
    }
  }

  const nextValidate = async () => {
    const t = skillMd.trim()
    if (t.length < 10) {
      toast.error('Paste a complete SKILL.md (with YAML frontmatter).')
      return
    }
    const { ok, vdata } = await validateNow(t)
    if (!ok) {
      const errs = Array.isArray(vdata.errors) ? vdata.errors : ['Invalid SKILL.md']
      toast.error(errs.slice(0, 3).join(' · '))
      return
    }
    setStep(2)
  }

  const submit = async () => {
    const t = skillMd.trim()
    if (t.length < 10) {
      toast.error('Paste a complete SKILL.md (with YAML frontmatter).')
      setStep(1)
      return
    }
    // Safety: if user jumps to upload without a successful validation, validate first.
    if (!vr?.valid) {
      const { ok } = await validateNow(t)
      if (!ok) return
    }
    setSaving(true)
    try {
      if (Array.isArray(vr?.warnings) && vr!.warnings!.length) {
        toast.message(vr!.warnings!.slice(0, 2).join(' · '))
      }
      const res = await fetch(`${API_BASE}api/skills`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ skill_md: t }),
      })
      const data = (await res.json().catch(() => ({}))) as { id?: string; error?: string; detail?: string }
      if (!res.ok) {
        throw new Error((data.error || data.detail || 'Could not create skill') as string)
      }
      toast.success('Skill created.')
      onCreated(String(data.id || ''))
      setStep(3)
      onClose()
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to create skill')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="modal-scrim" role="dialog" aria-modal="true" aria-labelledby="skillCreateTitle" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal create-modal">
        <div className="modal-head">
          <div>
            <div className="modal-title" id="skillCreateTitle">
              New skill
            </div>
            <div className="modal-sub">Paste SKILL.md content (YAML frontmatter + body). Same rules as file upload.</div>
          </div>
          <button type="button" className="modal-close" onClick={onClose} aria-label="Close">
            <svg viewBox="0 0 14 14" fill="none">
              <path d="M2 2L12 12M12 2L2 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </button>
        </div>
        <div className="modal-body">
          <div className="form-group">
            <div className="form-label">
              SKILL.md <span className="req">*</span>
            </div>
            <textarea
              className="form-input form-textarea"
              placeholder={'---\nname: my-skill-name\ndescription: What it does\n---\n\n## Instructions\n…'}
              value={skillMd}
              onChange={e => {
                setSkillMd(e.target.value)
                // If they edit after validating, reset the validation state.
                if (step !== 1) {
                  setStep(1)
                  setVr(null)
                }
              }}
              rows={14}
              style={{ minHeight: 220 }}
            />
          </div>
          {step >= 2 && (
            <ul className="suw-checklist" style={{ marginTop: 12 }}>
              {validating && (
                <li className="warn">
                  <span className="suw-ci">…</span> Validating…
                </li>
              )}
              {!validating && vr?.valid && (
                <>
                  <li className="ok">
                    <span className="suw-ci">✓</span> YAML frontmatter valid — name &amp; description found
                  </li>
                </>
              )}
              {!validating &&
                vr &&
                vr.valid === false &&
                (vr.errors || []).map((e, i) => (
                  <li key={`pte${i}`} className="bad">
                    <span className="suw-ci">✕</span> {e}
                  </li>
                ))}
              {!validating &&
                vr &&
                (vr.warnings || []).map((w, i) => (
                  <li key={`ptw${i}`} className="warn">
                    <span className="suw-ci">!</span> {w}
                  </li>
                ))}
            </ul>
          )}
          <div className="form-actions">
            <button type="button" className="btn-cancel" onClick={onClose}>
              Cancel
            </button>
            {step === 1 ? (
              <button type="button" className="btn-create" onClick={nextValidate} disabled={saving || validating}>
                {validating ? 'Validating…' : 'Next — validate'}
              </button>
            ) : (
              <>
                <button
                  type="button"
                  className="btn-cancel"
                  onClick={() => {
                    setStep(1)
                    setVr(null)
                  }}
                  disabled={saving || validating}
                >
                  Back
                </button>
                <button type="button" className="btn-create" onClick={submit} disabled={saving || validating || vr?.valid === false}>
                  {saving ? 'Uploading…' : 'Upload skill'}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export function SkillsView() {
  const [tab, setTab] = useState<SkillTab>('all')
  const [query, setQuery] = useState('')
  const [list, setList] = useState<SkillListItem[]>([])
  const [listLoading, setListLoading] = useState(true)
  const [listError, setListError] = useState<string | null>(null)

  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [detail, setDetail] = useState<SkillDetail | null>(null)
  const [detailLoading, setDetailLoading] = useState(false)
  const [assignments, setAssignments] = useState<AssignmentRow[] | null>(null)

  const [createOpen, setCreateOpen] = useState(false)
  const [uploadWizardOpen, setUploadWizardOpen] = useState(false)
  const [assignDrawer, setAssignDrawer] = useState<{ id: string; name: string } | null>(null)
  const [toggling, setToggling] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [skillToDelete, setSkillToDelete] = useState<{ id: string; name: string } | null>(null)

  const [isMobile, setIsMobile] = useState(false)
  const [mobilePane, setMobilePane] = useState<'list' | 'detail'>('list')

  useEffect(() => {
    if (typeof window === 'undefined') return
    const mq = window.matchMedia('(max-width: 900px)')
    const apply = () => setIsMobile(mq.matches)
    apply()
    mq.addEventListener?.('change', apply)
    return () => mq.removeEventListener?.('change', apply)
  }, [])

  const loadList = useCallback(async () => {
    setListLoading(true)
    setListError(null)
    try {
      const res = await fetch(`${API_BASE}api/skills`, { credentials: 'include' })
      if (res.status === 401) {
        setListError('Sign in to view skills.')
        setList([])
        return
      }
      if (!res.ok) throw new Error('Failed to load skills')
      const data = (await res.json()) as { skills?: SkillListItem[] }
      const skills = Array.isArray(data.skills) ? data.skills : []
      setList(skills)
      setSelectedId(prev => {
        if (prev && skills.some(s => s.id === prev)) return prev
        return skills[0]?.id ?? null
      })
    } catch {
      setListError('Could not load skills.')
      setList([])
    } finally {
      setListLoading(false)
    }
  }, [])

  useEffect(() => {
    void loadList()
  }, [loadList])

  const loadDetail = useCallback(async (id: string, isOwner: boolean) => {
    setDetailLoading(true)
    setDetail(null)
    setAssignments(null)
    try {
      const res = await fetch(`${API_BASE}api/skills/${encodeURIComponent(id)}`, { credentials: 'include' })
      if (!res.ok) {
        toast.error('Could not load skill details')
        return
      }
      const d = (await res.json()) as SkillDetail
      setDetail(d)
      if (isOwner) {
        const ar = await fetch(`${API_BASE}api/skills/${encodeURIComponent(id)}/assignments`, { credentials: 'include' })
        if (ar.ok) {
          const aj = (await ar.json()) as { assignments?: AssignmentRow[] }
          setAssignments(Array.isArray(aj.assignments) ? aj.assignments : [])
        }
      }
    } catch {
      toast.error('Could not load skill details')
    } finally {
      setDetailLoading(false)
    }
  }, [])

  const selectedListItem = list.find(s => s.id === selectedId)
  const selectedEnabled = selectedListItem?.is_enabled !== false

  useEffect(() => {
    if (!selectedId || !selectedListItem) {
      setDetail(null)
      setAssignments(null)
      return
    }
    void loadDetail(selectedId, selectedListItem.is_owner)
  }, [selectedId, selectedListItem, loadDetail])

  const filteredMine = useMemo(() => {
    const q = query.trim().toLowerCase()
    return list.filter(s => s.is_owner && (q === '' || s.name.toLowerCase().includes(q) || s.description.toLowerCase().includes(q)))
  }, [list, query])

  const filteredAssigned = useMemo(() => {
    const q = query.trim().toLowerCase()
    return list.filter(s => !s.is_owner && (q === '' || s.name.toLowerCase().includes(q) || s.description.toLowerCase().includes(q)))
  }, [list, query])

  const visibleMine = tab === 'assigned' ? [] : filteredMine
  const visibleAssigned = tab === 'mine' ? [] : filteredAssigned
  const showMineSection = tab !== 'assigned' && visibleMine.length > 0
  const showAssignedSection = tab !== 'mine' && visibleAssigned.length > 0
  const combined = useMemo(() => [...visibleMine, ...visibleAssigned], [visibleMine, visibleAssigned])

  useEffect(() => {
    if (combined.length === 0) {
      setSelectedId(null)
      return
    }
    if (!selectedId || !combined.some(s => s.id === selectedId)) setSelectedId(combined[0].id)
  }, [combined, selectedId])

  useEffect(() => {
    if (!isMobile) return
    // On mobile, if selection disappears (filters/search), fall back to list pane.
    if (!selectedId) setMobilePane('list')
  }, [isMobile, selectedId])

  const fm = useMemo(() => (detail ? parseSkillFrontmatter(detail.skill_md) : { meta: {}, body: '' }), [detail])
  const keywords = useMemo(() => {
    const k = fm.meta.keywords || fm.meta.triggers
    return splitKeywords(k)
  }, [fm.meta])

  const listRowSubtitle = (item: SkillListItem) => {
    if (item.is_owner) {
      const d = item.description?.trim()
      if (!d) return 'Your skill'
      return d.length > 52 ? `${d.slice(0, 52)}…` : d
    }
    return `Shared · owner ${item.owner_id.slice(0, 8)}…`
  }

  const setSkillEnabled = useCallback(
    async (skillId: string, enabled: boolean) => {
      setToggling(true)
      try {
        const res = await fetch(`${API_BASE}api/skills/${encodeURIComponent(skillId)}/enabled`, {
          method: 'PATCH',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ enabled }),
        })
        const data = (await res.json().catch(() => ({}))) as { detail?: string; error?: string; is_enabled?: boolean }
        if (!res.ok) throw new Error(data.detail || data.error || 'Could not update skill')

        const nextEnabled = typeof data.is_enabled === 'boolean' ? data.is_enabled : enabled
        setList(prev => prev.map(s => (s.id === skillId ? { ...s, is_enabled: nextEnabled } : s)))
        setDetail(prev => (prev && prev.id === skillId ? { ...prev, is_enabled: nextEnabled } : prev))
        toast.success(nextEnabled ? 'Skill enabled.' : 'Skill disabled.')
      } catch (e) {
        toast.error(e instanceof Error ? e.message : 'Failed to update skill')
      } finally {
        setToggling(false)
      }
    },
    []
  )

  const requestDeleteSkill = useCallback((id: string, name: string) => {
    setSkillToDelete({ id, name })
    setDeleteDialogOpen(true)
  }, [])

  const confirmDeleteSkill = useCallback(async () => {
    if (!skillToDelete) return
    const skillId = skillToDelete.id
    setDeleting(true)
    try {
      const res = await fetch(`${API_BASE}api/skills/${encodeURIComponent(skillId)}`, {
        method: 'DELETE',
        credentials: 'include',
      })
      const data = (await res.json().catch(() => ({}))) as { detail?: string; error?: string }
      if (!res.ok) throw new Error(data.detail || data.error || 'Could not delete skill')

      toast.success('Skill deleted.')
      setList(prev => prev.filter(s => s.id !== skillId))
      setSelectedId(prev => (prev === skillId ? null : prev))
      setDetail(prev => (prev?.id === skillId ? null : prev))
      setAssignments(null)
      if (isMobile) setMobilePane('list')
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to delete skill')
    } finally {
      setDeleting(false)
      setDeleteDialogOpen(false)
      setSkillToDelete(null)
    }
  }, [isMobile, skillToDelete])

  return (
    <div
      className={`skills-scope${isMobile ? ' is-mobile' : ''}${isMobile && mobilePane === 'detail' ? ' mobile-detail' : ''}${isMobile && mobilePane === 'list' ? ' mobile-list' : ''}`}
    >
      <ConfirmationDialog
        open={deleteDialogOpen}
        onOpenChange={open => {
          setDeleteDialogOpen(open)
          if (!open) setSkillToDelete(null)
        }}
        title="Delete skill?"
        description={
          skillToDelete
            ? `This will permanently delete “${skillToDelete.name}”. You can’t undo this action.`
            : 'This will permanently delete this skill. You can’t undo this action.'
        }
        confirmText={deleting ? 'Deleting…' : 'Delete'}
        variant="destructive"
        onConfirm={confirmDeleteSkill}
      />
      <div className="skills-list-panel">
        <div className="skills-list-head">
          <div className="skills-list-title">Skills</div>
          <button type="button" className="skills-btn-new" onClick={() => setUploadWizardOpen(true)}>
            + New skill
          </button>
        </div>
        <div className="skills-search-wrap">
          <div className="skills-search">
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
              <circle cx="6" cy="6" r="4.5" stroke="currentColor" strokeWidth="1.2" />
              <path d="M9 9l3.5 3.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
            </svg>
            <input type="search" placeholder="Search skills..." value={query} onChange={e => setQuery(e.target.value)} aria-label="Search skills" />
          </div>
        </div>
        <div className="skills-tabs" role="tablist" aria-label="Skill filters">
          {(['all', 'mine', 'assigned'] as const).map(t => (
            <button
              key={t}
              type="button"
              role="tab"
              aria-selected={tab === t}
              className={`skills-tab${tab === t ? ' active' : ''}`}
              onClick={() => setTab(t)}
            >
              {t === 'all' ? 'All' : t === 'mine' ? 'My skills' : 'Assigned'}
            </button>
          ))}
        </div>
        <div className="skills-list-scroll">
          {listLoading && <div style={{ padding: '20px', fontSize: 13, color: 'var(--text-muted)' }}>Loading skills…</div>}
          {listError && <div style={{ padding: '20px', fontSize: 13, color: '#f87171' }}>{listError}</div>}
          {!listLoading && !listError && showMineSection && (
            <>
              <div className="skills-section-label">My skills</div>
              {visibleMine.map(s => (
                <button
                  key={s.id}
                  type="button"
                  className={`skills-row${selectedId === s.id ? ' selected' : ''}`}
                  onClick={() => {
                    setSelectedId(s.id)
                    if (isMobile) setMobilePane('detail')
                  }}
                >
                  <div className="skills-row-ico" aria-hidden="true">
                    📦
                  </div>
                  <div className="skills-row-body">
                    <div className="skills-row-title">{s.name}</div>
                    <div className="skills-row-meta">{listRowSubtitle(s)}</div>
                  </div>
                  <span className="skills-badge owner">Owner</span>
                </button>
              ))}
            </>
          )}
          {!listLoading && !listError && showAssignedSection && (
            <>
              <div className="skills-section-label">Assigned to me</div>
              {visibleAssigned.map(s => (
                <button
                  key={s.id}
                  type="button"
                  className={`skills-row${selectedId === s.id ? ' selected' : ''}`}
                  onClick={() => {
                    setSelectedId(s.id)
                    if (isMobile) setMobilePane('detail')
                  }}
                >
                  <div className="skills-row-ico" aria-hidden="true">
                    📦
                  </div>
                  <div className="skills-row-body">
                    <div className="skills-row-title">{s.name}</div>
                    <div className="skills-row-meta">{listRowSubtitle(s)}</div>
                  </div>
                  <span className="skills-badge access">Access</span>
                </button>
              ))}
            </>
          )}
          {!listLoading && !listError && !showMineSection && !showAssignedSection && (
            <div style={{ padding: '24px 12px', textAlign: 'center', fontSize: 13, color: 'var(--text-muted)' }}>
              {list.length === 0 ? 'No skills yet. Create one or upload a .skill file.' : 'No skills match your search.'}
            </div>
          )}
        </div>
      </div>

      <div className="skills-detail">
        {detailLoading && selectedId && (
          <div className="skills-empty-detail" style={{ background: 'var(--bg-canvas)' }}>
            Loading…
          </div>
        )}
        {!detailLoading && detail && selectedListItem && (
          <div className="skills-detail-inner">
            {isMobile && (
              <button type="button" className="skills-back" onClick={() => setMobilePane('list')}>
                <span aria-hidden="true">←</span> Back
              </button>
            )}
            <div className="skills-detail-head">
              <div className="skills-detail-identity">
                <div className="skills-detail-icon" aria-hidden="true">
                  📦
                </div>
                <div className="skills-detail-titles">
                  <h2>{detail.name}</h2>
                  <p>
                    {selectedListItem.is_owner ? 'Owned by you' : 'Shared with you'}
                    {' · '}
                    ID: <span className="skills-detail-id">{detail.id}</span>
                  </p>
                </div>
              </div>
              <div className="skills-detail-actions">
                {selectedListItem.is_owner && detail && (
                  <>
                    <label className="skills-enable-toggle" title="Enable / disable this skill">
                      <span className="skills-enable-label">{selectedEnabled ? 'Enabled' : 'Disabled'}</span>
                      <span className="skills-switch">
                        <input
                          type="checkbox"
                          checked={selectedEnabled}
                          onChange={e => setSkillEnabled(detail.id, e.target.checked)}
                          disabled={toggling || deleting}
                          aria-label="Enable skill"
                        />
                        <span className="skills-switch-track" aria-hidden="true" />
                        <span className="skills-switch-thumb" aria-hidden="true" />
                      </span>
                    </label>
                    <button
                      type="button"
                      className="skills-action-btn primary"
                      onClick={() => setAssignDrawer({ id: selectedId!, name: detail.name })}
                      disabled={deleting}
                    >
                      Assign users
                    </button>
                    <button
                      type="button"
                      className="skills-action-btn danger"
                      onClick={() => requestDeleteSkill(detail.id, detail.name)}
                      disabled={deleting || toggling}
                      title="Delete skill"
                    >
                      {deleting ? 'Deleting…' : 'Delete'}
                    </button>
                  </>
                )}
              </div>
            </div>

            <div className="skills-block">
              <div className="skills-block-label">Description</div>
              <div className="skills-block-text">{detail.description}</div>
            </div>

            {keywords.length > 0 && (
              <div className="skills-block">
                <div className="skills-block-label">Trigger keywords</div>
                <div className="skills-pills">
                  {keywords.map(k => (
                    <span key={k} className="skills-pill">
                      {k}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {selectedListItem.is_owner && assignments && (
              <div className="skills-block">
                <div className="skills-block-label">Assigned users ({assignments.length})</div>
                <div className="skills-users">
                  {assignments.map((a, i) => (
                    <div key={a.user_id} className="skills-user">
                      <div className="skills-user-av" style={{ background: AV_COLORS[i % AV_COLORS.length] }}>
                        {initialsFromUser(a)}
                      </div>
                      <span className="skills-user-name">{a.username || a.email || a.user_id.slice(0, 8)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {!selectedListItem.is_owner && (
              <div className="skills-block">
                <div className="skills-block-label">Access</div>
                <div className="skills-block-text">You can use this skill in chat. Only the owner can change assignments.</div>
              </div>
            )}

            <div className="skills-block">
              <div className="skills-block-label">Skill components</div>
              <div className="skills-pills">
                <span className="skills-pill check">SKILL.md</span>
                {fm.meta.references ? <span className="skills-pill check">references</span> : null}
              </div>
            </div>

            <div className="skills-validation">
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
                <circle cx="9" cy="9" r="7.5" stroke="currentColor" strokeWidth="1.2" />
                <path d="M5.5 9l2.5 2.5L12.5 6" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              <span>
                {selectedEnabled ? 'Skill is active and available in chat for your account.' : 'Skill is disabled by the owner and will not run in chat.'}
              </span>
            </div>

            <div className="skills-detail-foot">
              <span>Uploaded {formatShortDate(detail.created_at)}</span>
              <span>Last edited {formatShortDate(detail.updated_at)}</span>
            </div>
          </div>
        )}
        {!detailLoading && !detail && selectedId && (
          <div className="skills-empty-detail">Could not load this skill.</div>
        )}
        {!selectedId && !listLoading && (
          <div className="skills-empty-detail">Select a skill or create one.</div>
        )}
      </div>

      {uploadWizardOpen && (
        <UploadSkillWizard
          onClose={() => setUploadWizardOpen(false)}
          onComplete={id => {
            void loadList()
            if (id) setSelectedId(id)
          }}
          onOpenAssign={(id, name) => {
            setUploadWizardOpen(false)
            setAssignDrawer({ id, name })
          }}
          onPasteInstead={() => {
            setUploadWizardOpen(false)
            setCreateOpen(true)
          }}
        />
      )}
      {createOpen && (
        <CreateSkillModal
          onClose={() => setCreateOpen(false)}
          onCreated={id => {
            void loadList()
            if (id) setSelectedId(id)
          }}
        />
      )}
      {assignDrawer && (
        <AssignUsersDrawer
          open
          skillId={assignDrawer.id}
          skillName={assignDrawer.name}
          onClose={() => setAssignDrawer(null)}
          onSaved={() => {
            if (assignDrawer.id) void loadDetail(assignDrawer.id, true)
            void loadList()
          }}
        />
      )}
    </div>
  )
}
